import { useEffect, useRef } from 'react';
import { isEmpty } from 'lodash-es';
import { useSelector } from 'umi';

// Strategic project specific page keys for auto-save
export const EDIT_PAGE_KEY_MAP = {
  // Business Management
  business_executive_summary_revenue_notes: 'businessExecutiveSummaryRevenueNotes',
  business_executive_summary_profit_notes: 'businessExecutiveSummaryProfitNotes',
  business_weekly_highlight: 'businessWeeklyHighlight',

  // Studio Management
  studio_overview_notes: 'studioOverviewNotes',
  studio_overview_phase_table: 'studioOverviewPhaseTable',
  studio_overview_pipeline_calendar: 'studioOverviewPipelineCalendar',

  studio_studios_notes: 'studioStudiosNotes',
  studio_studios_batch_notes: 'studioStudiosBatchNotes',
  studio_board_governance: 'studioBoardGovernance',
  studio_board_governance_meeting: 'studioBoardGovernanceMeeting',

  studio_live_notes: 'studioLiveTitlesNotes',
  studio_live_all: 'studioLiveTitlesAll',
  studio_live_finance: 'studioLiveTitlesFinance',

  studio_indev_headcount_card: 'studioIndevHeadcountCard',
  studio_indev_lifecycle_table: 'studioIndevLifecycleTable',
  studio_indev_gr_management: 'studioIndevGRManagement',
  studio_indev_notes: 'studioIndevNotes',
  studio_indev_all: 'studioIndevAll',

  // Publishing Management
  publishing_overview_notes: 'publishingOverviewNotes',
  publishing_overview_decision_points: 'publishingOverviewDecisionPoints',
  publishing_overview_gross_revenue: 'publishingOverviewGrossRevenue',

  publishing_mktcost_mkt_cost: 'publishingMktCostMktCost',
  publishing_mkt_cost_notes: 'publishingMktCostNotes',
  publishing_mktcost_mkt_budget_application_calendar: 'publishingMktCostMktBudgetApplicationCalendar',

  publishing_headcount_notes: 'publishingHeadcountNotes',
  publishing_headcount_appendix_notes: 'publishingHeadcountAppendixNotes',
  publishing_headcount_project_table: 'publishingHeadcountProjectTable',

  publishing_project_notes: 'publishingProjectNotes',
  publishing_project_review_updates: 'publishingProjectReviewUpdates',
};

// Same localStorage key as GPOP for consistency
const DBG_SAVE_AUTO_EDIT_PARAMS = '__dbg_save_auto_edit_params__';

export function useAutoSaveParams(props: any) {
  const { initialParams, isBegin = false } = props;

  // console.log('useAutoSaveParams-props', props);

  const { userInfo } = useSelector((state: any) => ({
    userInfo: state?.home?.userInfo,
  }));
  const { userid = '' } = userInfo || {};
  const timerRef = useRef<any>(null);
  const saveParamsCurRef = useRef(props); // 每次渲染时更新ref
  const pageKeyPaCurRef = useRef(props); // 每次渲染时更新ref
  const isEditedRef = useRef(props); // 每次渲染时更新ref
  const isChangeValueRef = useRef(props); // 每次渲染时更新ref
  const useridCurRef = useRef(props); // 每次渲染时更新ref
  // 因setInterval闭包问题，需要使用useRef
  saveParamsCurRef.current = props.saveParamsCur;
  pageKeyPaCurRef.current = props.pageKey;
  useridCurRef.current = userid;
  // pageKeyPaCurRef.current = `${props.pageKey}_${userid}`;
  isEditedRef.current = props.isEdited || false;
  isChangeValueRef.current = props.isChangeValue || false;

  // 获取当前saveParamsMap
  const getSaveParamsMap = () => {
    const savedParamsText = localStorage.getItem(DBG_SAVE_AUTO_EDIT_PARAMS);
    const savedParamsMap = savedParamsText ? JSON.parse?.(savedParamsText) : {};
    return savedParamsMap;
  };

  // 获取当前pageKey下的saveParams
  const getSaveParamsValue = (pageKey: string = pageKeyPaCurRef.current) => {
    const savedParamsMap = getSaveParamsMap();
    const pageKeyValue = `${pageKey}_${useridCurRef.current}`;
    const saveParamsValue = savedParamsMap?.[pageKeyValue] || {};
    return saveParamsValue;
  };

  const getSaveParamsValueCur = (pageKey: string) => {
    const savedParamsMap = getSaveParamsMap();
    const saveParamsValue = savedParamsMap?.[pageKey] || {};
    return saveParamsValue;
  };

  // 是否需要弹窗提醒有自动保存内容
  const showRestorePrompt = (pageKey: string = pageKeyPaCurRef.current) => {
    const pageKeyValue = `${pageKey}_${useridCurRef.current}`;
    const saveParamsValue = getSaveParamsValueCur(pageKeyValue);
    if (!isEmpty(saveParamsValue)) {
      return true;
    }
    return false;
  };

  // 保存参数到localStorage
  const saveParams = () => {
    const pageKeyValue = `${pageKeyPaCurRef.current}_${useridCurRef.current}`;
    const saveParamsValue = getSaveParamsValueCur(pageKeyValue);
    console.log('initialParams', initialParams);
    console.log('saveParamsCurRef.current', saveParamsCurRef.current);
    console.log('saveParamsValue', saveParamsValue);
    if (
      (!isEmpty(initialParams) && !isEmpty(saveParamsCurRef.current) && isChangeValueRef?.current) ||
      isEditedRef.current
    ) {
      if (JSON.stringify(initialParams) !== JSON.stringify(saveParamsCurRef.current)) {
        if (JSON.stringify(saveParamsCurRef.current) !== JSON.stringify(saveParamsValue)) {
          const saveParamsmap = getSaveParamsMap();

          const paramsMapValue = {
            ...saveParamsmap,
            [pageKeyValue]: saveParamsCurRef.current,
          };
          localStorage.setItem(DBG_SAVE_AUTO_EDIT_PARAMS, JSON.stringify(paramsMapValue));
        }
      }
    }
  };

  useEffect(() => {
    if (!isEmpty(initialParams) && isBegin) {
      // 设置定时器每5秒保存一次
      timerRef.current = setInterval(saveParams, 5000);

      return () => {
        clearInterval(timerRef.current);
      };
    }
  }, [initialParams, isBegin]);

  // 保存参数到localStorage
  const deleteSaveParamsByPageKey = (pageKey: string = pageKeyPaCurRef.current) => {
    const pageKeyValue = `${pageKey}_${useridCurRef.current}`;

    const saveParamsmap = getSaveParamsMap();
    delete saveParamsmap[pageKeyValue];
    localStorage.setItem(DBG_SAVE_AUTO_EDIT_PARAMS, JSON.stringify(saveParamsmap));
  };

  return {
    getSaveParamsValue,
    showRestorePrompt,
    deleteSaveParamsByPageKey,
  };
}
